import { Link, useLocation, useParams } from 'react-router-dom';

import { useDistribution } from '@/api/hooks.js';
import { Card, ErrorNotice, Loading, SectionHeading, StatusBadge } from '@/components/ui.js';
import { formatBytes, formatDateTime } from '@/lib/utils.js';

/**
 * Per-layout outcome of one run.
 *
 * `rejected` is deliberately distinct from `failed`: it means the creative did
 * not meet the platform's spec and no upload was attempted, which is something
 * the user can fix by re-rendering at a different size.
 *
 * Mounted in both route trees — an administrator reaches it from the history
 * table, a user from the project they just distributed — so the way back is
 * read from the path rather than fixed, to avoid sending an administrator into
 * the Experience page they never opened.
 */
export function DistributionPage() {
  const { distributionId = null } = useParams();
  const query = useDistribution(distributionId);
  const inAdmin = useLocation().pathname.startsWith('/admin');

  if (query.isLoading) return <Loading label="Loading distribution…" />;
  if (query.error) return <ErrorNotice error={query.error} />;
  if (!query.data) return null;

  const { distribution, items } = query.data;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={inAdmin ? '/admin/history' : '/experience'}
          className="text-sm text-muted hover:underline"
        >
          ← {inAdmin ? 'Distribution history' : 'All projects'}
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <h2 className="text-xl font-semibold">{distribution.sourceName}</h2>
          <StatusBadge status={distribution.status} />
        </div>
        <p className="mt-1 text-sm text-muted">
          Submitted {formatDateTime(distribution.createdAt)}
          {distribution.completedAt
            ? ` · finished ${formatDateTime(distribution.completedAt)}`
            : ''}
        </p>
      </div>

      {distribution.error ? (
        <div className="rounded-brand border border-danger-line bg-danger-surface p-4 text-sm text-danger">
          {distribution.error}
        </div>
      ) : null}

      <section>
        <SectionHeading title="Layouts" />
        <Card>
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Layout</th>
                <th className="px-4 py-3 font-medium">Size</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-line-subtle last:border-0">
                  <td className="px-4 py-3">
                    <p className="font-medium">{item.layoutName}</p>
                    {item.width && item.height ? (
                      <p className="text-xs text-muted">
                        {item.width} × {item.height} px
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-body">{formatBytes(item.sizeBytes)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3 text-body">
                    {item.remoteAssetId ? (
                      <code className="text-xs">{item.remoteAssetId}</code>
                    ) : (
                      <span className="text-xs text-muted">{item.error ?? '—'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>
    </div>
  );
}
